import { ExpiringSoon } from "./ExpiringSoon";
import { OrgSettings } from "./OrgSettings";
import { Navigate } from "react-router-dom";

import { Account } from "./Account";
import { Help } from "./Help";
import { ConsoleHome } from "./Home";
import { MyShift, MyShifts, VerificationReturn } from "./MyShifts";
import { AcceptInvitation } from "./AcceptInvitation";
import { ChooseOrg } from "./ChooseOrg";
import { ClientDetail, ClientList, NewClient } from "./Clients";
import { People } from "./People";
import { NewWorker, WorkerDetail, WorkerList } from "./Workers";
import { MyAvailability, WorkerBenchAvailability } from "./Availability";
import { MyProfile } from "./WorkerProfile";
import { CredentialTypes } from "./Credentials";
import { DocumentView } from "./Document";
import { PaymentBatch } from "./PaymentBatch";
import { ConfigurationPacks } from "./Configuration";
import { EventList, EventWorkspace, NewEvent } from "./Events";
import { NewRequirement, RequirementList } from "./Requirements";
import { ControlRoom } from "./Board";
import { LiveOps } from "./LiveOps";
import { Timesheets } from "./Timesheets";
import { Analytics } from "./Analytics";
import { OfflineSync } from "./OfflineSync";
import { Invoicing } from "./Invoicing";
import { ProofPacks } from "./ProofPacks";
import { Pay } from "./Pay";
import { Match } from "./Match";
import { Offers } from "./Offers";
import { AppealQueue, EventAttendance, MyRecord } from "./Reliability";
import { Incidents } from "./Incidents";
import { RiskAssessments } from "./RiskAssessments";
import { EventMessages } from "./EventMessages";
import { Expenses } from "./Expenses";
import { MyExpenses } from "./MyExpenses";
import { Compliance } from "./Compliance";
import { Privacy } from "./Privacy";
import { RiskRadar } from "./RiskRadar";
import { ConsoleNotifications, PortalAccount } from "./Notifications";
import { GateCheck, RoleDefinitions } from "./Roles";
import { SignUp } from "./SignUp";
import { Message } from "./Message";
import { OidcCallback } from "./OidcCallback";
import { SignIn } from "./SignIn";
import { EnterWithLink, ResetPassword, Unsubscribe, VerifyEmail } from "./TokenLink";
import { Apply } from "./Apply";
import { Applicants } from "./Applicants";
import { ApiAndWebhooks } from "./ApiAndWebhooks";
import { PlatformReviews } from "./PlatformReviews";
import {
  PortalApprovalsScreen,
  PortalChangesScreen,
  PortalDocumentsScreen,
  PortalProgramme,
  PortalReadinessScreen,
  PortalRosterScreen,
  PortalWallScreen,
  PortalWorkersScreen,
} from "./Portal";
import { homePathFor } from "../session/access";
import { useSession } from "../session/SessionProvider";
import { SURFACE_PREFIX, type Surface } from "../system/navigation";
import { Tabs } from "../design/Tabs";
import { PreSessionLocale, useT } from "../i18n/LocaleProvider";
import type { ScreenId } from "../system/screens";
import { HubRequests } from "./HubRequests";

// ---------------------------------------------------------------------------
// ONE ELEMENT PER SCREEN, IN A FILE OF ITS OWN.
//
// It lived in App.tsx until the demo gallery needed it, and moving it was NOT
// tidying — it was the fix for a measured defect. The gallery renders the real
// elements, so it imported this map from App.tsx, and App.tsx imports the
// gallery to route it. That cycle stopped rollup dead-code-eliminating the
// gallery out of a production build: the component itself was dropped and its
// FIXTURE MODULE WAS NOT, so a production bundle shipped a fabricated
// organisation, its people and its clients.
//
// Measured, not reasoned about: `grep Northgate` over the production bundle
// found it, after the same grep for the gallery's own marker found nothing —
// which is exactly the half-success that would have read as "tree-shaking
// works" to anybody who checked one string.
//
// So: no module under web/src/demo may be imported by anything outside it, and
// nothing outside may import from it. scripts/verify-demo-absent.sh asserts
// that direction as well as grepping the bundle, because the grep only catches
// it after somebody has already shipped a string worth grepping for.
// ---------------------------------------------------------------------------

/** Exported because App.tsx renders it when a path belongs to no surface. */
// Tour T-116. The expiry window and the 21-row catalogue were one 2,100 px page.
function CredentialTabs() {
  const { t } = useT();
  return (
    <Tabs
      label={t("Credentials")}
      tabs={[
        { id: "cred-expiring", label: t("Expiring soon"), content: <ExpiringSoon /> },
        { id: "cred-types", label: t("Credentials"), content: <CredentialTypes /> },
      ]}
    />
  );
}

// Tour T-114. The role list and the shift check were one 1,633 px page; the check is a second job.
function RolesTabs() {
  const { t } = useT();
  return (
    <Tabs
      label={t("Roles")}
      tabs={[
        { id: "roles-all", label: t("Roles"), content: <RoleDefinitions /> },
        { id: "roles-check", label: t("Shift check"), content: <GateCheck /> },
      ]}
    />
  );
}

export function NotFoundInside({ surface = "console" }: { surface?: Surface }) {
  const { t } = useT();
  // S62. The way out is the surface's own home — a worker or a client cannot open
  // the console — and only the console has a screen search. The worker's is in
  // their language (no provider, as on the console and portal, is English).
  if (surface !== "console") {
    return (
      <section className="panel">
        <div className="panel-head">
          <h2>{t("Not found")}</h2>
        </div>
        <p>
          {t("There is nothing at this address — it may have been mistyped, or the link that brought you here may be out of date.")}{" "}
          <a href={SURFACE_PREFIX[surface]}>{surface === "worker" ? t("Go to my shifts") : t("Go to the overview")}</a>
        </p>
      </section>
    );
  }
  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t("Not found")}</h2>
      </div>
      {/* ★ THIS SAID "it may belong to a part of Verbunk that is not built yet",
          which was the honest reading at V4 when the console was a shell and
          almost every address was unbuilt. With the console at thirty-five
          screens a mistyped or stale address is overwhelmingly the likelier
          cause, and defaulting to "your product is unfinished" is the wrong
          thing to tell somebody who has paid for it. The link matters more than
          the sentence: a dead end with no way out is the defect. */}
      <p>
        {t("There is nothing at this address — it may have been mistyped, or the link that brought you here may be out of date.")}{" "}
        <a href="/console">{t("Go to the overview")}</a>{t(", or")}{" "}
        <a href="/console/help">{t("search every screen")}</a>.
      </p>
    </section>
  );
}

/** S75: a public address nobody is signed in behind reads in the browser's language. */
function NotFound() {
  return (
    <PreSessionLocale>
      <NotFoundPublic />
    </PreSessionLocale>
  );
}

function NotFoundPublic() {
  const { t } = useT();
  return (
    <Message tone="neutral" status={t("Not found")} title={t("There is nothing at this address")} action={{ label: t("Go to sign in"), to: "/sign-in" }} />
  );
}

/** S75. The wait before the session is known, in the browser's language (App's RootRedirect too). */
export function PreSessionLoading() {
  return (
    <PreSessionLocale>
      <PreSessionLoadingMessage />
    </PreSessionLocale>
  );
}

function PreSessionLoadingMessage() {
  const { t } = useT();
  return <Message tone="neutral" status={t("Working")} title={t("Loading…")} />;
}

/**
 * Somebody who is already signed in has no use for the sign-in screen, and
 * showing it to them is how a person ends up entering a password they did not
 * need to. Bounce them to their own surface.
 */
function SignedOutOnly({ children }: { children: React.ReactNode }) {
  const { state, me } = useSession();
  if (state === "loading") return <PreSessionLoading />;
  if (me?.authenticated) {
    const home = homePathFor(me);
    return <Navigate to={home ?? SURFACE_PREFIX.console} replace />;
  }
  return <>{children}</>;
}

/**
 * ONE ELEMENT PER SCREEN IN system/screens.ts, AND THE COMPILER SAYS SO.
 *
 * `Record<ScreenId, ReactNode>` is exhaustive: a screen added to the table with
 * no element here does not compile, and an element here for a screen that is
 * not in the table does not compile either. The route table and the router
 * cannot drift, which is what makes the table trustworthy enough for the demo
 * gallery to be checked against it.
 *
 * The wrappers stay attached to the element rather than moving into the table —
 * SignedOutOnly is a behaviour, not a route, and a table that carried
 * behaviours would be a second router.
 */
export const SCREEN_ELEMENTS: Record<ScreenId, React.ReactNode> = {
  // V17a. Not wrapped in a permission guard here: the screen itself asks whether the
  // viewer is platform staff, and the API refuses everybody else twice regardless.
  "console.platform-reviews": <PlatformReviews />,
  "public.sign-in": <SignedOutOnly><SignIn /></SignedOutOnly>,
  "public.sign-up": <SignedOutOnly><SignUp /></SignedOutOnly>,
  // NOT wrapped in SignedOutOnly, and deliberately. A person already signed in
  // to one agency may be invited by another; bouncing them to their own console
  // would make the link they were sent look broken. Accepting rotates their
  // session, which is the point.
  "public.accept-invitation": <AcceptInvitation />,
  // NOT wrapped in SignedOutOnly, for the reason accept-invitation is not: this
  // screen SIGNS SOMEBODY IN, so the guard would fire the moment it succeeded
  // and race the navigation the screen performs itself.
  "public.oidc-callback": <OidcCallback />,
  "public.choose-organisation": <ChooseOrg />,
  "public.enter": <EnterWithLink />,
  "public.reset": <ResetPassword />,
  "public.verify": <VerifyEmail />,
  "public.unsubscribe": <Unsubscribe />,
  "public.apply": <Apply />,
  "public.not-found": <NotFound />,

  "console.home": <ConsoleHome />,
  "console.clients": <ClientList />,
  "console.clients.new": <NewClient />,
  "console.clients.detail": <ClientDetail />,
  "console.workers": <WorkerList />,
  "console.workers.new": <NewWorker />,
  "console.workers.detail": <WorkerDetail />,
  "console.people": <People />,
  // Sweep S22: what expires across the bench, above the catalogue it belongs to.
  "console.credential-types": <CredentialTabs />,
  // V31a. THE SHIFT CHECK IS ON THE SCREEN THAT CLAIMED IT.
  //
  // `GateCheck` was exported by Roles.tsx and rendered by nothing — V8's
  // credential verdict table, V15's four conflict classes and the sentence that
  // explains each, none of it reachable. The gallery registry has described this
  // panel since V8 and the description was the only place it existed.
  "console.roles": (
    <RolesTabs />
  ),
  // Sweep S21b.
  "console.documents.detail": <DocumentView />,
  "console.payment-batches.detail": <PaymentBatch />,
  "console.configuration": <ConfigurationPacks />,
  "console.events": <EventList />,
  "console.hub-requests": <HubRequests />,
  "console.events.new": <NewEvent />,
  "console.events.detail": <EventWorkspace />,
  "console.workers.availability": <WorkerBenchAvailability />,
  "console.events.requirements": <RequirementList />,
  "console.events.requirements.new": <NewRequirement />,
  "console.events.crew": <ControlRoom />,
  "console.events.live": <LiveOps />,
  "console.events.timesheets": <Timesheets />,
  "console.events.proof": <ProofPacks />,
  "console.events.invoicing": <Invoicing />,
  "console.analytics": <Analytics />,
  "console.sync": <OfflineSync />,
  "console.events.pay": <Pay />,
  "console.events.match": <Match />,
  "console.events.offers": <Offers />,
  "console.events.attendance": <EventAttendance />,
  "console.events.incidents": <Incidents />,
  "console.events.risk-assessment": <RiskAssessments />,
  "console.events.messages": <EventMessages />,
  "console.events.expenses": <Expenses />,
  "console.events.risk": <RiskRadar />,
  "console.people.appeals": <AppealQueue />,
  "console.people.applicants": <Applicants />,
  "worker.record": <MyRecord />,
  "console.compliance": <Compliance />,
  "console.privacy": <Privacy />,
  "console.help": <Help />,
  "console.account": <Account />,
  "console.settings": <OrgSettings />,
  "console.settings.api": <ApiAndWebhooks />,
  "console.notifications": <ConsoleNotifications />,
  "console.not-found": <NotFoundInside />,

  // V24. THE STUB IS GONE, exactly as V16 removed the worker app's. PortalHome
  // printed the signed-in identity and a "this surface is coming" note, which was
  // honest at V4 and became a second front page the moment there was a real one.
  "portal.home": <PortalProgramme />,
  "portal.event": <PortalReadinessScreen />,
  "portal.event.wall": <PortalWallScreen />,
  "portal.event.roster": <PortalRosterScreen />,
  "portal.event.changes": <PortalChangesScreen />,
  "portal.event.approvals": <PortalApprovalsScreen />,
  "portal.event.documents": <PortalDocumentsScreen />,
  // D12. NOT under an event: a reference outlives the roster it was minted from,
  // which is the whole point of asking about a date that has no event yet.
  "portal.workers": <PortalWorkersScreen />,
  "portal.account": <PortalAccount />,
  "portal.not-found": <NotFoundInside surface="portal" />,

  // V16. THE STUB IS GONE. `WorkerHome` printed a heading and a progress note;
  // MyShifts is the screen §V16 asked for and the front page keeps its id, so no
  // link, no nav entry and no gallery cell had to move.
  "worker.home": <MyShifts />,
  "worker.shift": <MyShift />,
  "worker.availability": <MyAvailability />,
  "worker.profile": <MyProfile />,
  "worker.expenses": <MyExpenses />,
  "worker.verification-return": <VerificationReturn />,
  "worker.not-found": <NotFoundInside surface="worker" />,
};
